import React from "react";
import { ArrowRight, ScanLine, ShieldCheck, Activity, Users, KeyRound, CheckCircle2, GraduationCap, Eye, Building2, UserCog, Baby, Loader2, X, ChevronDown, LayoutGrid, ClipboardCheck, HeartPulse, ClipboardList } from "lucide-react";
import Logo from "@/components/app/Logo";
import LogoSplash from "@/components/app/LogoSplash";
import { ZaviaHeroBg, ShiningLogo, HeaderVideoBg, HeroPhrases } from "@/components/app/ZaviaHero";
import EmailAuthPanel from "@/components/app/EmailAuthPanel";
import { useAuth } from "@/lib/auth";
import { Navigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { resetThemeToStock } from "@/lib/themes";


const HERO_BG_ROTATION = [
  "https://customer-assets.emergentagent.com/job_261cac39-09da-4fa6-9c4f-5c742cee548a/artifacts/e2w9m1bn_AA566155-F9C0-4DD7-9CC9-B138840D8CAF.png",
  "https://customer-assets.emergentagent.com/job_261cac39-09da-4fa6-9c4f-5c742cee548a/artifacts/rwj4bpzx_9410A594-3DE7-4D52-B272-12FEA6DE222E.png",
  "https://customer-assets.emergentagent.com/job_261cac39-09da-4fa6-9c4f-5c742cee548a/artifacts/i53zp30v_IMG_3695.jpeg",
  "https://customer-assets.emergentagent.com/job_261cac39-09da-4fa6-9c4f-5c742cee548a/artifacts/gly172yl_IMG_3696.jpeg",
  "https://customer-assets.emergentagent.com/job_261cac39-09da-4fa6-9c4f-5c742cee548a/artifacts/354vwwbl_IMG_3699.jpeg",
];
const HERO_PHRASES = [
  "Control sin fricción.",
  "Control de pagos.",
  "Chat interno.",
  "Áreas administrativas seguras.",
  "Pagos seguros.",
  "Recordatorio de pagos.",
  "Programación de salida anticipada.",
];
const COTIZAR_URL = "https://instabio.cc/Uaicardmx2026";

function RotatingText({ phrases, className }) {
  const [i, setI] = React.useState(0);
  const [vis, setVis] = React.useState(true);
  React.useEffect(() => {
    const id = setInterval(() => {
      setVis(false);
      setTimeout(() => { setI((p) => (p + 1) % phrases.length); setVis(true); }, 450);
    }, 2600);
    return () => clearInterval(id);
  }, [phrases.length]);
  return (
    <span
      className={className}
      data-testid="hero-rotating-text"
      style={{ display: "inline-block", transition: "opacity .45s ease, transform .45s ease", opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(10px)" }}
    >
      {phrases[i]}
    </span>
  );
}

function HeroBgRotation({ images }) {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    if (images.length < 2) return;
    const id = setInterval(() => setI((p) => (p + 1) % images.length), 4000);
    return () => clearInterval(id);
  }, [images.length]);
  return (
    <>
      {images.map((src, idx) => (
        <img key={idx} src={src} alt="" className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ease-in-out" style={{ opacity: idx === i ? 1 : 0 }} />
      ))}
    </>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle(inviteCode) {
  if (inviteCode && inviteCode.trim()) {
    sessionStorage.setItem("uaicard_invite_code", inviteCode.trim().toUpperCase());
  }
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const IMG = "https://static.prod-images.emergentagent.com/jobs/261cac39-09da-4fa6-9c4f-5c742cee548a/images/";
const HERO_IMG = IMG + "00dae9e25891151ecf7cee620936d05b244ec9f7eca721019bdb46f8163c1c06.jpeg";
const FEATURES = [
  { img: "2c4011aa401e414cdcef79fdd1ef2e661b9a0865bbecb707db0f13149e281e3b.jpeg", tag: "Asistencia NFC", title: "Un toque y queda registrado", desc: "Entradas y salidas en segundos, con aviso inmediato a los papás." },
  { img: "35b0762c62126d0cd491a58f1e4164e5439d42e19691e7283085615f17625bad.jpeg", tag: "Portal de padres", title: "La escuela en su bolsillo", desc: "Avisos, pagos, calificaciones y chat con maestros desde el celular." },
  { img: "60ac37da67b4a8f3eea07a99a0cc5d03fc62003f85bbf5f43a4590c87b33e7a4.jpeg", tag: "Portal docente", title: "Menos papeleo, más clase", desc: "Pase de lista, calificaciones, planeaciones y clubs en un solo lugar." },
  { img: "40dfa7a3855df6868f449b8bf0f75a6882709eae4dc78577dccbd642fba18cd6.jpeg", tag: "Evaluaciones", title: "Exámenes que se califican solos", desc: "Plantillas por nivel, banco de preguntas y estadísticas precisas." },
  { img: "daadac95ba8395c4e877ee6a6e89b64695a7d2314e82a052cb685ec66c6cfe1d.jpeg", tag: "Enfermería", title: "Cada atención, documentada", desc: "Expediente médico con foto, alergias, visitas y reportes." },
  { img: "b656711ceb0c5b5499f0dac5b1c0bb7950e8280a1b1d0fb1ea6000e660149df1.jpeg", tag: "Dirección", title: "Todo tu colegio en un tablero", desc: "Cobranza, admisiones, bajas y reportes en tiempo real." },
];

function HeroShowcase({ onLogin, onDemo }) {
  return (
    <div className="relative" data-testid="hero-showcase">
      <style>{`@keyframes zvPop { 0% { opacity:0; transform: translateY(14px) scale(.96); } 100% { opacity:1; transform: none; } }
        @keyframes zvBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes zvRing { 0% { box-shadow: 0 0 0 0 rgba(59,130,246,.55); } 100% { box-shadow: 0 0 0 22px rgba(59,130,246,0); } }`}</style>
      <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-blue-500/40 via-cyan-400/10 to-transparent blur-2xl" />
      <div className="relative rounded-[24px] overflow-hidden ring-1 ring-white/20 shadow-[0_30px_80px_-20px_rgba(0,0,0,.7)] aspect-[4/5] sm:aspect-[5/6]">
        <img src={HERO_IMG} alt="Alumno registrando su entrada con tarjeta NFC" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#04112e] via-[#04112e]/30 to-transparent" />
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/95 backdrop-blur rounded-2xl pl-2 pr-4 py-2 shadow-xl" style={{ animation: "zvPop .6s .3s both, zvBob 5s 1s ease-in-out infinite" }}>
          <span className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center" style={{ animation: "zvRing 1.8s ease-out infinite" }}><CheckCircle2 className="w-5 h-5" /></span>
          <span className="leading-tight"><span className="block text-[11px] text-nardo-500">Entrada registrada</span><span className="block text-sm font-bold text-navy">7:42 a.m. · Aviso enviado</span></span>
        </div>
        <div className="absolute top-24 right-4 hidden sm:flex items-center gap-2 bg-[#0b1f4d]/85 backdrop-blur text-white rounded-2xl px-3 py-2 shadow-xl ring-1 ring-white/15" style={{ animation: "zvPop .6s .8s both, zvBob 6s 1.5s ease-in-out infinite" }}>
          <ScanLine className="w-4 h-4 text-cyan-300" /><span className="text-xs font-semibold">NFC · 0.3 s</span>
        </div>
        <div className="absolute bottom-0 inset-x-0 p-6 sm:p-7" style={{ animation: "zvPop .7s .5s both" }}>
          <div className="text-[10px] uppercase tracking-[0.35em] text-cyan-300 mb-2">Así se vive ZAVIA</div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-white leading-tight">Llega a la escuela y su familia lo sabe al instante.</h2>
          <p className="text-sm text-blue-100/80 mt-2">Asistencia, avisos, pagos y calificaciones conectados en una sola plataforma.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={onLogin} data-testid="hero-showcase-login" className="group inline-flex items-center gap-2 bg-white text-navy px-5 py-2.5 rounded-full text-sm font-bold shadow-lg hover:-translate-y-0.5 transition-transform">Iniciar sesión <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></button>
            <button onClick={onDemo} data-testid="hero-showcase-demo" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white ring-1 ring-white/30 backdrop-blur px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"><Eye className="w-4 h-4" /> Ver demo</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureShowcase() {
  return (
    <section className="relative bg-[#04112e] overflow-hidden" data-testid="feature-showcase">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-[0.35em] text-cyan-300 mb-3 flex items-center gap-3"><span className="w-10 h-px bg-cyan-300" /> Funciones reales</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight text-white leading-[1.05]">Una plataforma.<br /><span className="text-blue-300">Todo tu colegio conectado.</span></h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <article key={f.tag} className={`group relative rounded-3xl overflow-hidden ring-1 ring-white/10 bg-[#0b1f4d] shadow-2xl `} data-testid={`feature-${i}`}>
              <div className={`relative overflow-hidden aspect-[4/3]`}>
                <img src={IMG + f.img} alt={f.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-[center_20%] transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#04112e] via-[#04112e]/35 to-transparent" />
                <span className="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-white/90 text-navy px-3 py-1 rounded-full">{f.tag}</span>
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <h3 className="font-display font-bold text-xl text-white">{f.title}</h3>
                  <p className="text-sm text-blue-100/80 mt-1">{f.desc}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const DEMO_PORTALS = [
  { key: "member", title: "Área del usuario", desc: "Dirección: asistencias, alumnos, cobranza, reportes y más.", icon: Building2 },
  { key: "staff", title: "Área administrativa", desc: "Perfil del personal administrativo del colegio.", icon: UserCog },
  { key: "coordinacion", title: "Portal de coordinación", desc: "Revisión de asistencias, planeaciones, calificaciones y bitácora.", icon: ClipboardCheck },
  { key: "docente", title: "Portal de docentes", desc: "Pase de lista, planeaciones y grupos del maestro.", icon: GraduationCap },
  { key: "alumno", title: "Portal del alumno", desc: "Aula virtual, cuaderno digital, biblioteca y mensajes.", icon: Users },
  { key: "padres", title: "Portal de padres", desc: "Asistencia de su hijo, pagos y comunicados.", icon: Baby },
  { key: "enfermeria", title: "Portal de enfermería", desc: "Expedientes, visitas, incidentes, avisos a padres y reportes.", icon: HeartPulse },
  { key: "evaluaciones", title: "Portal de evaluaciones", desc: "Exámenes digitales, plantillas, banco de preguntas y estadísticas.", icon: ClipboardList },
];

function DemoModal({ onClose, onPick, loading }) {
  React.useEffect(() => {
    const b = document.body.style, h = document.documentElement.style;
    const prev = [b.overflow, h.overflow, b.touchAction];
    b.overflow = "hidden"; h.overflow = "hidden"; b.touchAction = "none";
    return () => { [b.overflow, h.overflow, b.touchAction] = prev; };
  }, []);
  return (
    <div className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4 overscroll-none touch-none" data-testid="demo-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden shadow-2xl touch-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-navy text-white px-6 py-5 relative">
          <button onClick={onClose} data-testid="demo-close" className="absolute top-4 right-4 text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 flex items-center gap-2"><Eye className="w-4 h-4" /> Modo demostración</div>
          <h2 className="font-display font-bold text-2xl mt-1">Explora todo el sistema</h2>
          <p className="text-sm text-nardo-200 mt-1">Colegio de muestra, aislado y seguro. No necesitas registrarte. Cada sesión dura 5 horas.</p>
        </div>
        <div className="p-4 space-y-2 flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}>
          {DEMO_PORTALS.map((p) => {
            const Icon = p.icon;
            const busy = loading === p.key;
            return (
              <button key={p.key} onClick={() => onPick(p.key)} disabled={!!loading} data-testid={`demo-portal-${p.key}`}
                className="w-full text-left flex items-center gap-4 border border-nardo-200 hover:border-navy hover:bg-nardo-50 rounded-xl p-3 transition-colors disabled:opacity-50">
                <span className="w-11 h-11 rounded-lg bg-navy/5 text-navy flex items-center justify-center shrink-0">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
                </span>
                <span className="min-w-0">
                  <span className="block font-semibold text-navy">{p.title}</span>
                  <span className="block text-xs text-nardo-500">{p.desc}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-nardo-400 ml-auto shrink-0" />
              </button>
            );
          })}
        </div>
        <div className="px-6 py-3 bg-nardo-50 text-[11px] text-nardo-500 text-center border-t border-nardo-100">
          🔒 Los datos de la demo están separados del sistema real. Nada de lo que hagas afecta a colegios reales.
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user, loading, setUser, checkAuth } = useAuth();
  const [code, setCode] = React.useState("");
  const [verified, setVerified] = React.useState(null); // {school_name} or null
  const [checking, setChecking] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [authError, setAuthError] = React.useState(null);
  const [ownerMode, setOwnerMode] = React.useState(false);
  const [emailMode, setEmailMode] = React.useState(false);
  const [demoOpen, setDemoOpen] = React.useState(false);
  const [demoLoading, setDemoLoading] = React.useState(null);
  const [portalsOpen, setPortalsOpen] = React.useState(false);
  const [portalName, setPortalName] = React.useState(null);

  const startDemo = async (portal) => {
    if (demoLoading) return;
    setDemoLoading(portal);
    try {
      const { data } = await api.post("/demo/start", { portal });
      try { sessionStorage.setItem("uaicard_demo", "1"); if (portal === "evaluaciones") sessionStorage.setItem("zv_tab_docente", "evaluaciones"); } catch { /* noop */ }
      window.location.href = data.redirect;
    } catch (err) {
      setDemoLoading(null);
      setAuthError("No se pudo abrir la demostración. Intenta de nuevo.");
      setDemoOpen(false);
    }
  };

  React.useEffect(() => {
    resetThemeToStock(); // el Landing/login siempre en colores stock de ZAVIA
    // Al abrir en móvil: sin teclado abierto y desde arriba para que se vea el logo.
    try { document.activeElement?.blur?.(); window.scrollTo(0, 0); } catch { /* noop */ }
    const err = sessionStorage.getItem("uaicard_auth_error");
    if (err) {
      setAuthError(err);
      sessionStorage.removeItem("uaicard_auth_error");
    }
  }, []);

  if (!loading && user) {
    const target = user.role === "owner" ? "/admin" : user.role === "docente" ? "/docente" : (user.role === "staff" && user.staff_kind === "coordinacion") ? "/direccion" : (user.role === "staff" && user.staff_kind === "enfermeria") ? "/enfermeria" : "/dashboard";
    let after = null;
    try { after = sessionStorage.getItem("zv_after_login"); sessionStorage.removeItem("zv_after_login"); } catch { /* noop */ }
    const canEval = after && user.role !== "owner" && user.staff_kind !== "enfermeria";
    return <Navigate to={canEval ? after : target} replace />;
  }

  const validateCode = async (e) => {
    e?.preventDefault();
    setError(null); setChecking(true);
    try {
      const { data } = await api.post("/auth/precheck-code", { code });
      setVerified({ school_name: data.school_name, login_bg_url: data.login_bg_url, logo_url: data.logo_url });
    } catch (err) {
      setError(err.response?.data?.detail || "Código inválido");
      setVerified(null);
    } finally { setChecking(false); }
  };

  return (
    <div className="min-h-screen bg-nardo-50">
      <LogoSplash sessionKey="landing" />
      <div className="h-[3px] w-full bg-navy" />

      {demoOpen && (
        <DemoModal onClose={() => setDemoOpen(false)} onPick={startDemo} loading={demoLoading} />
      )}

      <div className="relative z-40">
      <HeaderVideoBg />
      <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
        <Logo variant="light" />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="relative">
            <button
              data-testid="portales-dropdown-button"
              onClick={() => setPortalsOpen((v) => !v)}
              className="text-xs uppercase tracking-[0.2em] text-white bg-navy hover:bg-navy-light px-4 py-2 rounded-md transition-colors inline-flex items-center gap-1.5"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Portales
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${portalsOpen ? "rotate-180" : ""}`} />
            </button>
            {portalsOpen && (
              <>
                <div className="fixed inset-0 z-[40]" onClick={() => setPortalsOpen(false)} />
                <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-2xl border border-nardo-200 overflow-hidden z-[50]" data-testid="portales-dropdown-menu">
                  <Link to="/padres" data-testid="header-parent-portal" onClick={() => setPortalsOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-nardo-50 transition-colors">
                    <Baby className="w-4 h-4 text-navy/70" /> Portal de padres
                  </Link>
                  <Link to="/alumno" data-testid="header-student-portal" onClick={() => setPortalsOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-nardo-50 transition-colors border-t border-nardo-100">
                    <Users className="w-4 h-4 text-navy/70" /> Portal de alumno
                  </Link>
                  <button data-testid="header-docente-portal" onClick={() => { setPortalsOpen(false); setPortalName("Portal de docentes"); setEmailMode(true); setVerified(null); setOwnerMode(false); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-nardo-50 transition-colors border-t border-nardo-100">
                    <GraduationCap className="w-4 h-4 text-navy/70" /> Portal de docentes
                  </button>
                  <button data-testid="header-coord-portal" onClick={() => { setPortalsOpen(false); setPortalName("Portal de coordinación"); setEmailMode(true); setVerified(null); setOwnerMode(false); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-nardo-50 transition-colors border-t border-nardo-100">
                    <ClipboardCheck className="w-4 h-4 text-navy/70" /> Portal de coordinación
                  </button>
                  <button data-testid="header-nurse-portal" onClick={() => { setPortalsOpen(false); setPortalName("Portal de enfermería"); setEmailMode(true); setVerified(null); setOwnerMode(false); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-nardo-50 transition-colors border-t border-nardo-100">
                    <HeartPulse className="w-4 h-4 text-rose-500" /> Portal de enfermería
                  </button>
                  <button data-testid="header-eval-portal" onClick={() => { setPortalsOpen(false); setPortalName("Portal de evaluaciones"); try { sessionStorage.setItem("zv_after_login", "/evaluaciones"); sessionStorage.setItem("zv_tab_docente", "evaluaciones"); } catch { /* noop */ } setEmailMode(true); setVerified(null); setOwnerMode(false); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-nardo-50 transition-colors border-t border-nardo-100">
                    <ClipboardCheck className="w-4 h-4 text-violet-600" /> Portal de evaluaciones
                  </button>
                  <button data-testid="header-staff-portal" onClick={() => { setPortalsOpen(false); setPortalName("Área administrativa"); setEmailMode(true); setVerified(null); setOwnerMode(false); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-nardo-50 transition-colors border-t border-nardo-100">
                    <UserCog className="w-4 h-4 text-navy/70" /> Área administrativa
                  </button>
                  <button data-testid="user-quick-login" onClick={() => { setPortalsOpen(false); setEmailMode(true); setVerified(null); setOwnerMode(false); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-navy hover:bg-nardo-50 transition-colors border-t border-nardo-100">
                    <Building2 className="w-4 h-4 text-navy/70" /> Área del usuario
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            data-testid="guest-demo-button"
            onClick={() => setDemoOpen(true)}
            className="text-xs uppercase tracking-[0.2em] text-navy bg-amber-300 hover:bg-amber-400 px-4 py-2 rounded-md transition-colors inline-flex items-center gap-1.5 font-semibold shadow-sm"
          >
            <Eye className="w-3.5 h-3.5" /> Soy invitado
          </button>
          <button
            data-testid="header-login-button"
            onClick={() => setOwnerMode(true)}
            className="text-xs uppercase tracking-[0.25em] text-navy bg-white/90 hover:bg-white border border-white px-4 py-2 rounded-md transition-colors"
          >
            Soy propietario
          </button>
        </div>
      </header>
      </div>

      {/* Hero con imagen de fondo */}
      <section className="relative overflow-hidden">
        {/* Imagen de fondo + overlay para resaltar el contenido */}
        <div className="absolute inset-0 z-0">
          {verified?.login_bg_url ? (
            <>
              <img src={`${API}${verified.login_bg_url}`} alt="" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1a2233]/95 via-[#1a2233]/80 to-[#1a2233]/55" />
            </>
          ) : <ZaviaHeroBg />}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 flex flex-col justify-center">
            {!verified?.login_bg_url && (
              <div className="flex items-center gap-6 sm:gap-10">
                <ShiningLogo />
                <HeroPhrases />
              </div>
            )}
            <div className="text-xs uppercase tracking-[0.3em] text-blue-200 mb-6 flex items-center gap-3">
              <span className="w-10 h-px bg-blue-300" /> Plataforma escolar NFC
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white drop-shadow-lg">
              Asistencia con un toque.<br />
              <RotatingText phrases={HERO_PHRASES} className="text-blue-200" />
            </h1>
            <p className="mt-6 text-base sm:text-lg text-nardo-100/90 max-w-xl leading-relaxed drop-shadow">
              ZAVIA es una plataforma integral de identificación y administración institucional altamente segura y escalable.
            </p>
            <p className="mt-4 text-lg sm:text-xl font-semibold text-blue-200 drop-shadow">
              Tu colegio a la vanguardia
            </p>
          </div>

          {/* Access gate panel */}
          <div className="lg:col-span-5 relative">
            <div className={(!verified && !ownerMode && !emailMode) ? "relative" : "relative bg-white border border-nardo-200 rounded-xl p-6 sm:p-8 shadow-2xl ring-1 ring-black/5"} data-testid="access-gate">
            {verified?.logo_url && (
              <div className="flex justify-center mb-5" data-testid="school-logo">
                <img src={`${API}${verified.logo_url}`} alt="Logo del colegio" className="h-16 w-auto max-w-[180px] object-contain" />
              </div>
            )}
            {(verified || ownerMode || emailMode) && <div className="absolute -top-px left-0 right-0 h-[2px] bg-navy" />}

            {authError && (
              <div className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3" data-testid="auth-error-banner">
                {authError}
              </div>
            )}

            {!verified && !ownerMode && !emailMode && (
              <HeroShowcase onLogin={() => { setPortalName(null); setEmailMode(true); }} onDemo={() => setDemoOpen(true)} />
            )}

            {emailMode && (
              <EmailAuthPanel
                onSuccess={(u) => { setUser(u); checkAuth(); }}
                portalLabel={portalName}
                onBack={() => { setEmailMode(false); setError(null); setPortalName(null); }}
                onGoogle={() => loginWithGoogle("")}
              />
            )}

            {(verified || ownerMode) && !emailMode && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  {verified ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <ShieldCheck className="w-4 h-4 text-navy" />}
                  <div className="text-[10px] uppercase tracking-[0.3em] text-navy">
                    {verified ? "Paso 2 · Acceso" : "Acceso de propietario"}
                  </div>
                </div>
                <h2 className="font-display font-bold text-2xl text-navy mb-1">
                  {verified ? "Código aceptado" : "Entrar como propietario"}
                </h2>
                {verified?.school_name && (
                  <p className="text-sm text-nardo-500 mb-2">Licencia para: <span className="text-navy font-medium">{verified.school_name}</span></p>
                )}
                <p className="text-sm text-nardo-500 mb-6">Continúa con tu cuenta de Google asociada.</p>

                <button
                  data-testid="hero-login-button"
                  onClick={() => loginWithGoogle(verified ? code : "")}
                  className="w-full group inline-flex items-center justify-center gap-3 bg-navy hover:bg-navy-light text-white px-8 py-4 rounded-md transition-all duration-200 hover:-translate-y-[1px] shadow-sm"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0"><path fill="#fff" d="M21.35 11.1H12v2.9h5.35c-.23 1.42-1.7 4.18-5.35 4.18-3.22 0-5.85-2.66-5.85-5.94S8.78 6.3 12 6.3c1.84 0 3.07.78 3.78 1.45l2.58-2.49C16.83 3.96 14.66 3 12 3 6.99 3 3 6.99 3 12s3.99 9 9 9c5.2 0 8.64-3.65 8.64-8.79 0-.59-.07-1.05-.16-1.51z"/></svg>
                  Entrar con Google
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>

                <button
                  onClick={() => { setVerified(null); setOwnerMode(false); setCode(""); }}
                  className="mt-4 text-xs uppercase tracking-[0.25em] text-nardo-500 hover:text-navy"
                  data-testid="back-to-code"
                >← Cambiar opción</button>
              </>
            )}
            </div>
          </div>
        </div>
      </section>

      <FeatureShowcase />

      <footer className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex items-center justify-between text-xs text-nardo-500">
        <span>© {new Date().getFullYear()} ZAVIA</span>
        <span className="uppercase tracking-[0.2em]">Acceso por licencia</span>
      </footer>
    </div>
  );
}
